import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PhotoCarousel = ({ images, autoPlay = true, interval = 5000 }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    const nextSlide = useCallback(() => {
        setCurrentIndex((prevIndex) => 
            prevIndex === images.length - 1 ? 0 : prevIndex + 1
        );
    }, [images.length]);

    const prevSlide = () => {
        setCurrentIndex((prevIndex) => 
            prevIndex === 0 ? images.length - 1 : prevIndex - 1
        );
    };

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    useEffect(() => {
        if (autoPlay && !isHovered) {
            const timer = setInterval(nextSlide, interval);
            return () => clearInterval(timer);
        }
    }, [autoPlay, isHovered, interval, nextSlide]);

    return (
        <div 
            className="relative w-full overflow-hidden rounded-2xl"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            data-testid="photo-carousel"
        >
            {/* Main Image Container */}
            <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden">
                {images.map((image, index) => (
                    <div
                        key={index}
                        className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                            index === currentIndex 
                                ? 'opacity-100 scale-100' 
                                : 'opacity-0 scale-105'
                        }`}
                    >
                        <img
                            src={image.url}
                            alt={image.caption || `Slide ${index + 1}`}
                            className="w-full h-full object-cover"
                        />
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#3D4435]/80 via-transparent to-[#3D4435]/30" />
                        
                        {/* Caption */}
                        {image.caption && (
                            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                                <p className="text-white text-lg md:text-2xl font-['Playfair_Display'] italic max-w-3xl">
                                    "{image.caption}"
                                </p>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Navigation Arrows */}
            <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#3D4435]/70 hover:bg-[#C9A227] text-white flex items-center justify-center transition-all duration-300 backdrop-blur-sm"
                aria-label="Previous slide"
                data-testid="carousel-prev"
            >
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </button>
            <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#3D4435]/70 hover:bg-[#C9A227] text-white flex items-center justify-center transition-all duration-300 backdrop-blur-sm"
                aria-label="Next slide"
                data-testid="carousel-next"
            >
                <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>

            {/* Dots Indicator */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                {images.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`transition-all duration-300 rounded-full ${
                            index === currentIndex 
                                ? 'w-8 h-2 bg-[#C9A227]' 
                                : 'w-2 h-2 bg-white/50 hover:bg-white/80'
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                        data-testid={`carousel-dot-${index}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default PhotoCarousel;
